import { useEffect, useState } from "react";
import axios from "axios";
import { X, ChevronRight, MessageSquare, Ban, Search, Mic, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  in_asteptare: "În Așteptare Apel",
  nu_a_raspuns: "Nu a răspuns",
  nr_indisponibil: "Nr indisponibil",
  nu_raspunde: "Nu răspunde",
  de_acord_cadou_da: "CADOU DA",
  de_acord_cadou_da_fara_mesaj: "CADOU DA (fără mesaj)",
  de_acord_cadou_nu: "CADOU NU",
  refuza_postare: "Nu e de acord să posteze",
};

/**
 * StatusFlowModal — wizard pentru schimbarea statusului unui client din
 * pagina Apeluri Recenzii. Flow Feb 2026:
 *
 *   În Așteptare Apel → { Nu a răspuns | Nr indisponibil | A răspuns }
 *   A răspuns → Vrea recenzie? { Da | Nu (Nu e de acord să posteze) }
 *   A răspuns → Da → 3 outcome-uri:
 *     • Da – Trimite Mesaj    → trimite auto template WhatsApp + bifă
 *                               mesaj recenzie + status CADOU DA (verde)
 *     • Da – Fără Mesaj       → status CADOU DA (albastru)
 *     • Nu – Căutăm în eMAG   → status CADOU NU (mov)
 *   Nu a răspuns → { Nu răspunde | Nr indisponibil | A răspuns → ... }
 */
export default function StatusFlowModal({ phone, currentStatus, orderId, productImageUrl, productName, onClose, onSaved }) {
  const [step, setStep] = useState("root"); // root | agree | cadou | refuse_reason
  const [saving, setSaving] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");
  // Feb 2026 — buton „Afișează Speach" la pașii agree / cadou / refuse_reason.
  // Speach-urile mapate se citesc din settings; conținutul complet vine din
  // colecția `apeluri_recenzii_speeches` (encoded după `speech_id`).
  const [speechMap, setSpeechMap] = useState({ a_raspuns: null, da: null, nu: null });
  const [expandedSpeech, setExpandedSpeech] = useState(null); // "a_raspuns" | "da" | "nu" | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sRes, listRes] = await Promise.all([
          axios.get(`${API}/apeluri-recenzii/settings`),
          axios.get(`${API}/apeluri-recenzii/settings/speeches`),
        ]);
        if (cancelled) return;
        const set = sRes.data?.settings || {};
        const byId = new Map((listRes.data?.speeches || []).map((s) => [s.speech_id, s]));
        setSpeechMap({
          a_raspuns: byId.get(set.speech_a_raspuns_id) || null,
          da: byId.get(set.speech_da_id) || null,
          nu: byId.get(set.speech_nu_id) || null,
        });
      } catch { /* silent — buton pur și simplu nu apare dacă nu putem citi */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const apply = async (newStatus, notes) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/apeluri-recenzii/${encodeURIComponent(phone)}/status`, {
        status: newStatus,
        notes: notes || "",
      });
      const nextDate = r.data?.next_action_date;
      toast.success(`Status actualizat: ${LABELS[newStatus]}`, {
        description: nextDate ? `Reprogramat pentru ${nextDate}` : undefined,
      });
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  // „Da – Trimite Mesaj" = 3 side-effects atomice (rezistente la eșec):
  //   1. Set status = CADOU DA (verde) — mereu se aplică
  //   2. Bifează „message_sent" pe toate comenzile clientului
  //   3. Trimite template WhatsApp „Mesaj Recenzie" prin configul CRM izolat
  // Dacă WhatsApp eșuează (config incomplet, template dezactivat), status +
  // bifa rămân aplicate → operatorul le poate trimite manual.
  const applyDaTrimiteMesaj = async () => {
    setSaving(true);
    try {
      // 1) Status
      await axios.patch(`${API}/apeluri-recenzii/${encodeURIComponent(phone)}/status`, {
        status: "de_acord_cadou_da",
      });
      // 2) Bifează message_sent pe toate comenzile clientului
      try {
        const list = await axios.get(`${API}/apeluri-recenzii`, { params: { older_than_days: 0 } });
        const client = (list.data?.clients || []).find((c) => c.phone === phone);
        const orders = client?.orders || [];
        await Promise.all(orders.map((o) =>
          axios.patch(`${API}/apeluri-recenzii/${encodeURIComponent(phone)}/orders/${encodeURIComponent(o.order_id)}/reminders`,
            { reminder: "message_sent", value: true }).catch(() => null),
        ));
      } catch (_e) { /* non-critical */ }
      // 3) Trimite template WhatsApp Mesaj Recenzie prin configul CRM izolat.
      //    Numele template-ului predefinit se citește din settings-ul CRM
      //    (Setări → Config Recenzie). Fallback: `mesaj_recenzie`.
      try {
        const settingsR = await axios.get(`${API}/apeluri-recenzii/settings`);
        const tplName = settingsR.data?.settings?.mesaj_recenzie_template_name?.trim() || "mesaj_recenzie";
        const tplLang = settingsR.data?.settings?.mesaj_recenzie_language?.trim() || "ro";
        await axios.post(`${API}/apeluri-recenzii/whatsapp/send-template`, {
          to: phone, template_name: tplName, language: tplLang,
        });
        toast.success("CADOU DA · Mesaj trimis", { description: `Template: ${tplName}` });
      } catch (waErr) {
        toast.warning("Status setat, dar mesaj NEtrimis", {
          description: waErr.response?.data?.detail || waErr.message,
        });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="status-flow-modal">
      <div
        className={`w-full ${expandedSpeech ? "max-w-2xl" : "max-w-md"} rounded-xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl transition-[max-width] duration-200`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100">
            Actualizează status apel
          </h3>
          <button onClick={onClose} className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mb-3 text-[11px] text-zinc-500">
          <span className="text-zinc-400">Telefon:</span> <span className="font-mono text-zinc-200">{phone}</span>
          <br />
          <span className="text-zinc-400">Actual:</span> {LABELS[currentStatus] || currentStatus}
        </div>

        {/* Feb 2026 — poza produsului + nr comandă vizibile la toți pașii wizard-ului. */}
        {(orderId || productImageUrl) && (
          <div
            data-testid="status-flow-order-header"
            className="mb-3 flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/40 p-2"
          >
            {productImageUrl ? (
              <img
                src={productImageUrl}
                alt={productName || "produs"}
                className="h-12 w-12 flex-shrink-0 rounded border border-zinc-800 object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="status-flow-product-image"
              />
            ) : (
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded border border-zinc-800 bg-zinc-950 text-[9px] text-zinc-600">
                fără poză
              </div>
            )}
            <div className="min-w-0 flex-1">
              {orderId && (
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Comandă</div>
              )}
              {orderId && (
                <div className="truncate font-mono text-sm font-semibold text-orange-200" data-testid="status-flow-order-id">
                  #{orderId}
                </div>
              )}
              {productName && (
                <div className="truncate text-[11px] text-zinc-400" title={productName} data-testid="status-flow-product-name">
                  {productName}
                </div>
              )}
            </div>
          </div>
        )}

        {step === "root" && (
          <div className="space-y-2">
            <p className="text-[11px] text-zinc-500">Selectează noul status:</p>
            {(currentStatus === "in_asteptare" || currentStatus === "nu_a_raspuns") && (
              <>
                {currentStatus === "in_asteptare" && (
                  <StatusButton onClick={() => apply("nu_a_raspuns")} disabled={saving} testId="btn-nu-a-raspuns">
                    Nu a răspuns
                    <span className="text-[10px] text-zinc-500">Se afișează mâine în „Pentru azi" (după 00:00)</span>
                  </StatusButton>
                )}
                {currentStatus === "nu_a_raspuns" && (
                  <StatusButton onClick={() => apply("nu_raspunde")} disabled={saving} testId="btn-nu-raspunde">
                    Nu răspunde (final)
                    <span className="text-[10px] text-zinc-500">Iese din „Pentru azi"</span>
                  </StatusButton>
                )}
                <StatusButton onClick={() => apply("nr_indisponibil")} disabled={saving} testId="btn-nr-indisponibil">
                  Nr indisponibil
                </StatusButton>
                <StatusButton onClick={() => setStep("agree")} testId="btn-a-raspuns">
                  A răspuns
                  <ChevronRight className="ml-auto h-3.5 w-3.5" />
                </StatusButton>
              </>
            )}
            {/* Finale — allow revert to in_asteptare */}
            {["nu_raspunde", "nr_indisponibil", "de_acord_cadou_da",
              "de_acord_cadou_da_fara_mesaj", "de_acord_cadou_nu",
              "refuza_postare"].includes(currentStatus) && (
              <StatusButton onClick={() => apply("in_asteptare")} disabled={saving} testId="btn-revert">
                Revert la „În Așteptare Apel"
              </StatusButton>
            )}
          </div>
        )}

        {step === "agree" && (
          <div className="space-y-2">
            <p className="text-[11px] text-zinc-400">E de acord să posteze recenzie?</p>
            <SpeechToggle
              which="a_raspuns"
              speech={speechMap.a_raspuns}
              expanded={expandedSpeech === "a_raspuns"}
              onToggle={() => setExpandedSpeech(expandedSpeech === "a_raspuns" ? null : "a_raspuns")}
            />
            <StatusButton onClick={() => setStep("cadou")} testId="btn-acord-da">
              Da <ChevronRight className="ml-auto h-3.5 w-3.5" />
            </StatusButton>
            <StatusButton onClick={() => setStep("refuse_reason")} testId="btn-acord-nu">
              Nu (Nu e de acord să posteze) <ChevronRight className="ml-auto h-3.5 w-3.5" />
            </StatusButton>
            <button onClick={() => setStep("root")} className="text-[11px] text-zinc-500 hover:text-zinc-300">← Înapoi</button>
          </div>
        )}

        {step === "refuse_reason" && (
          <div className="space-y-2">
            <SpeechToggle
              which="nu"
              speech={speechMap.nu}
              expanded={expandedSpeech === "nu"}
              onToggle={() => setExpandedSpeech(expandedSpeech === "nu" ? null : "nu")}
            />
            <p className="text-[11px] text-zinc-400">
              Motiv <span className="text-zinc-600">(opțional)</span>
            </p>
            <textarea
              value={refuseReason}
              onChange={(e) => setRefuseReason(e.target.value)}
              placeholder="Ex: nu are cont eMAG, nu vrea să lase review public…"
              rows={3}
              data-testid="refuse-reason-input"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
            />
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setStep("agree")}
                className="text-[11px] text-zinc-500 hover:text-zinc-300"
              >← Înapoi</button>
              <button
                type="button"
                onClick={() => apply("refuza_postare", refuseReason.trim())}
                disabled={saving}
                data-testid="btn-refuse-ok"
                className="rounded-md border border-orange-500/50 bg-orange-500/90 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-400 disabled:opacity-50"
              >
                {saving ? "Se salvează…" : "OK"}
              </button>
            </div>
          </div>
        )}

        {step === "cadou" && (
          <div className="space-y-2">
            <SpeechToggle
              which="da"
              speech={speechMap.da}
              expanded={expandedSpeech === "da"}
              onToggle={() => setExpandedSpeech(expandedSpeech === "da" ? null : "da")}
            />
            <p className="text-[11px] text-zinc-400">Ce se întâmplă acum?</p>
            <StatusOptionButton
              onClick={applyDaTrimiteMesaj}
              disabled={saving}
              testId="btn-cadou-da-trimite-mesaj"
              icon={<MessageSquare className="h-3.5 w-3.5 text-emerald-300" />}
              accent="emerald"
              title="Da – Trimite Mesaj"
              subtitle="Trimite automat template Mesaj Recenzie + bifează checkbox-ul, status CADOU DA (verde)"
            />
            <StatusOptionButton
              onClick={() => apply("de_acord_cadou_da_fara_mesaj")}
              disabled={saving}
              testId="btn-cadou-da-fara-mesaj"
              icon={<Ban className="h-3.5 w-3.5 text-sky-300" />}
              accent="sky"
              title="Da – Fără Mesaj"
              subtitle="Status CADOU DA (albastru), fără trimitere WhatsApp"
            />
            <StatusOptionButton
              onClick={() => apply("de_acord_cadou_nu")}
              disabled={saving}
              testId="btn-cadou-nu-cautam"
              icon={<Search className="h-3.5 w-3.5 text-violet-300" />}
              accent="violet"
              title="Nu – Căutăm în eMAG"
              subtitle="Status CADOU NU (mov) — nu vrea cadou, verificăm recenzia pe eMAG"
            />
            <button onClick={() => setStep("agree")} className="text-[11px] text-zinc-500 hover:text-zinc-300">← Înapoi</button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusButton({ children, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="flex w-full items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-left text-sm text-zinc-100 hover:border-orange-500/50 hover:bg-orange-500/10 disabled:opacity-50"
    >
      <div className="flex flex-1 flex-col">{children}</div>
    </button>
  );
}

const ACCENT_CLASSES = {
  emerald: "hover:border-emerald-500/60 hover:bg-emerald-500/10",
  sky: "hover:border-sky-500/60 hover:bg-sky-500/10",
  violet: "hover:border-violet-500/60 hover:bg-violet-500/10",
};

function StatusOptionButton({ onClick, disabled, testId, icon, accent, title, subtitle }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`flex w-full items-start gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2.5 text-left text-sm text-zinc-100 disabled:opacity-50 ${ACCENT_CLASSES[accent] || ""}`}
    >
      <span className="mt-0.5">{icon}</span>
      <div className="flex flex-1 flex-col">
        <span className="text-[13px] font-medium">{title}</span>
        <span className="text-[10.5px] leading-snug text-zinc-500">{subtitle}</span>
      </div>
    </button>
  );
}

/**
 * SpeechToggle — buton verde „Afișează speach" + panel expandat cu textul.
 * Randează null dacă nu există speach mapat pentru punctul curent (per user Feb 2026).
 */
function SpeechToggle({ which, speech, expanded, onToggle }) {
  if (!speech) return null;
  return (
    <div data-testid={`speech-toggle-${which}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`speech-toggle-btn-${which}`}
        className="inline-flex w-full items-center justify-between gap-2 rounded-md border border-emerald-500/50 bg-emerald-500/15 px-3 py-1.5 text-[12px] font-semibold uppercase tracking-wider text-emerald-100 hover:bg-emerald-500/25"
        title={speech.title}
      >
        <span className="flex items-center gap-1.5">
          <Mic className="h-3.5 w-3.5" />
          {expanded ? "Ascunde speach" : "Afișează speach"}
          <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-emerald-100">
            {speech.title}
          </span>
        </span>
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {expanded && (
        <pre
          data-testid={`speech-body-${which}`}
          className="mt-1.5 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 font-sans text-[13px] leading-relaxed text-zinc-100"
        >{speech.body}</pre>
      )}
    </div>
  );
}
